import { type ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BasicLayout from "@container/layout/basic";
import { CheckCircleIcon, ClockIcon, LinkIcon, Loader2Icon, ShieldXIcon } from "lucide-react";
import { activateReviewer } from "@service/reviewer";
import { setCookie } from "@util/cookie";

type ActivateState = "loading" | "success" | "expired" | "used" | "deactivated" | "error";

const STATE_CONTENT: Record<
    Exclude<ActivateState, "loading">,
    { icon: ReactNode; title: string; description: string; iconBg: string }
> = {
    success: {
        icon: <CheckCircleIcon className="h-10 w-10 text-green-500" />,
        iconBg: "bg-green-50",
        title: "You're verified and ready",
        description:
            "Your identity has been confirmed. When your HR team shares a candidate profile link with you, simply open it to view the profile.",
    },
    expired: {
        icon: <ClockIcon className="h-10 w-10 text-amber-500" />,
        iconBg: "bg-amber-50",
        title: "This activation link has expired",
        description:
            "This link is valid for 72 hours and has since expired. Contact your HR team to be sent a new invitation.",
    },
    used: {
        icon: <LinkIcon className="h-10 w-10 text-gray-500" />,
        iconBg: "bg-gray-100",
        title: "This link has already been used",
        description:
            "This activation link is one-time use and has already been verified. Open a candidate profile link shared with you to continue.",
    },
    deactivated: {
        icon: <ShieldXIcon className="h-10 w-10 text-red-500" />,
        iconBg: "bg-red-50",
        title: "Your access has been removed",
        description:
            "Your reviewer access has been deactivated. Contact your HR team if you believe this is a mistake.",
    },
    error: {
        icon: <ShieldXIcon className="h-10 w-10 text-red-500" />,
        iconBg: "bg-red-50",
        title: "Something went wrong",
        description:
            "We couldn't verify your activation link. Please try clicking the link in your email again, or contact your HR team.",
    },
};

const ERROR_CODE_MAP: Record<string, Exclude<ActivateState, "loading" | "success" | "error">> = {
    EXPIRED: "expired",
    TOKEN_EXPIRED: "expired",
    ALREADY_USED: "used",
    TOKEN_USED: "used",
    DEACTIVATED: "deactivated",
};

export default function ReviewerActivatePage() {
    const [searchParams] = useSearchParams();
    const [state, setState] = useState<ActivateState>("loading");

    const token = searchParams.get("token") ?? "";
    const reviewerId = searchParams.get("reviewer_id") ?? "";

    useEffect(() => {
        if (!token || !reviewerId) {
            setState("expired");
            return;
        }

        activateReviewer({ token, reviewer_id: reviewerId })
            .then((resp) => {
                if (resp.code === 200) {
                    const sessionDays = resp.data?.session_duration_days ?? 30;
                    setCookie("REVIEWER_TOKEN", resp.data?.token || "", sessionDays);
                    setState("success");
                    return;
                }
                const errorCode = resp.errors?.[0] ?? "";
                const mapped = ERROR_CODE_MAP[errorCode];
                setState(mapped ?? "error");
            })
            .catch(() => {
                setState("error");
            });
    }, []);

    return (
        <BasicLayout hideNavbarMenu isHideLoginButton showFooter={false}>
            <div className="w-full max-w-md mx-auto px-4 py-10">
                <div className="border border-gray-200 rounded-xl shadow-sm bg-white px-8 py-10">
                    {state === "loading" ? (
                        <div className="flex flex-col items-center text-center space-y-5">
                            <div className="p-4 rounded-full bg-blue-50">
                                <Loader2Icon className="h-10 w-10 text-blue-500 animate-spin" />
                            </div>
                            <div className="space-y-2">
                                <h1 className="text-2xl font-bold text-gray-900">Verifying your link…</h1>
                                <p className="text-sm text-gray-500 leading-relaxed">
                                    Please wait while we confirm your activation link.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center text-center space-y-5">
                            <div className={`p-4 rounded-full ${STATE_CONTENT[state].iconBg}`}>
                                {STATE_CONTENT[state].icon}
                            </div>
                            <div className="space-y-2">
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {STATE_CONTENT[state].title}
                                </h1>
                                <p className="text-sm text-gray-500 leading-relaxed">
                                    {STATE_CONTENT[state].description}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </BasicLayout>
    );
}
